import { FormMultiSelect } from '@ipfabric/ui-components/FormMultiSelect/FormMultiSelect';
import { FormSegmentedButton } from '@ipfabric/ui-components/FormSegmentedButton/FormSegmentedButton';
import { FormSwitch } from '@ipfabric/ui-components/FormSwitch/FormSwitch';
import { FormTextArea } from '@ipfabric/ui-components/FormTextArea/FormTextArea';
import { SegmentButton } from '@ipfabric/ui-components/SegmentedButton/SegmentButton/SegmentButton';
import type { SiteSeparationAttributeTransformation } from '@ipfabric/utils/schema/siteSeparation/siteSeparationAttributeTransformation';
import { useEffect } from 'react';
import { useController, useWatch } from 'react-hook-form';
import {
  type RuleEditorContentProps,
  type RuleEditorValues,
  TRANSFORMATION_OPTIONS,
  VENDOR_OPTIONS,
} from './RuleEditor.types';

const allVendors = VENDOR_OPTIONS.map((option) => option.value);

export const SlugRuleEditorContent = ({ control }: RuleEditorContentProps) => {
  const { field: enabledVendorsField } = useController({
    control,
    name: 'enabledVendors',
  });
  const applyToAllVendors = useWatch({
    control,
    name: 'applyToAllVendors',
  });
  const enabledVendors = useWatch({
    control,
    name: 'enabledVendors',
  });

  useEffect(() => {
    if (!applyToAllVendors) {
      return;
    }
    const hasAllVendorsSelected =
      enabledVendors.length === allVendors.length &&
      allVendors.every((vendor) => enabledVendors.includes(vendor));
    if (hasAllVendorsSelected) {
      return;
    }

    enabledVendorsField.onChange(allVendors);
  }, [applyToAllVendors, enabledVendors, enabledVendorsField.onChange]);

  return (
    <>
      <FormSwitch
        control={control}
        label='Include cloud devices discovered via SSH or Vendor API'
        name='applyToCloudInstances'
      />
      <FormSwitch
        control={control}
        label='Apply to all vendors'
        name='applyToAllVendors'
      />
      {!applyToAllVendors && (
        <FormMultiSelect
          control={control}
          description='Choose one or more vendors.'
          label='Selected vendor(s)'
          name='enabledVendors'
          options={VENDOR_OPTIONS}
        />
      )}
      <FormSegmentedButton.Single<
        RuleEditorValues,
        SiteSeparationAttributeTransformation
      >
        control={control}
        label='Text transformation'
        name='transformation'
      >
        {TRANSFORMATION_OPTIONS.map(({ label, value }) => (
          <SegmentButton key={value} value={value} className='flex-1'>
            {label}
          </SegmentButton>
        ))}
      </FormSegmentedButton.Single>
      <FormTextArea
        control={control}
        dataTestId='site-separation-note'
        label='Rule description (optional)'
        name='note'
      />
    </>
  );
};
